import { defineStore } from 'pinia'
import { ref, computed, nextTick } from 'vue'
import api from '@/utils/api'
import { API_ROUTES } from '@/const/apiRoutes'
import type { ProgressResponse, PersonalNote, CurrentWeekResponse } from '@/types/curriculum'

export const useCurriculumStore = defineStore('curriculum', () => {
  const curriculum = ref<ProgressResponse>({ backlog: {}, future: {}, progress: [] })
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const activeWeek = ref<CurrentWeekResponse | null>(null)
  const isUpdating = ref(false)
  const personalNotes = ref<Record<number, PersonalNote>>({}) // progressId -> { id, notes }
  const isNotesLoading = ref(false)
  const notesError = ref<string | null>(null)
  const expeditionSteps = ref<any>(null) // New state for expedition steps
  const isStepsLoading = ref(false)     // Loading state for steps
  const stepsError = ref<string | null>(null) // Error state for steps
  const isReviewSubmitting = ref(false) // Loading state for review submission
  const reviewError = ref<string | null>(null) // Error state for review submission
  const isReordering = ref(false) // Loading state for reordering

  // Computed property for backlog count
  const backlogCount = computed(() => {
    if (!curriculum.value.backlog) {
      return 0;
    }
    
    try {
      // Get all arrays from backlog object and flatten them
      const backlogWeeks = Object.keys(curriculum.value.backlog);
      
      if (backlogWeeks.length === 0) {
        return 0;
      }
      
      const tasks = Object.values(curriculum.value.backlog)
        .reduce((acc: any[], weekTasks: any) => {
          return [...acc, ...weekTasks];
        }, []);
      
      return tasks.length;
    } catch (error) {
      console.error('Error calculating backlog count:', error);
      return 0;
    }
  })

  const fetchCurrentWeekProgress = async (expeditionId: string) => {
    isLoading.value = true
    error.value = null

    try {
      // get-current-week only returns { current_week } - we need progress data from get-progress-by-week
      const weekResponse = await api.get(API_ROUTES.GET_PROGRESS_OF_CURRENT_WEEK(expeditionId))
      const currentWeekNum = weekResponse.data?.current_week ?? 1
      const progressResponse = await api.get(
        API_ROUTES.GET_PROGRESS_BY_WEEK(String(currentWeekNum), expeditionId)
      )
      activeWeek.value = progressResponse.data as CurrentWeekResponse
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch current week'
      console.error('Error fetching current week:', err)
    } finally {
      isLoading.value = false
    }
  }

  const fetchProgressByWeek = async (weekId: string, expeditionId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_PROGRESS_BY_WEEK(weekId, expeditionId))
      activeWeek.value = response.data as CurrentWeekResponse
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch curriculum by week'
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      fetchCurrentWeekProgress(expeditionId)
    } finally {
      isLoading.value = false
    }
  }

  const fetchCurriculumList = async (expeditionId: string) => {
    isLoading.value = true
    error.value = null

    try {
      const response = await api.get(API_ROUTES.GET_CURRICULUM_LIST(expeditionId))
      // Transform array response to expected object structure
      if (Array.isArray(response.data)) {
        const transformedData = {
          backlog: {},
          future: {}
        }

        // Here we could implement proper data transformation logic
        // based on the actual structure of the API response
        curriculum.value = transformedData as ProgressResponse
      } else {
        curriculum.value = response.data
      }

      // Let WeekNavigator handle the week-specific data fetching
      // Removed: if (!activeWeek.value) { await fetchCurrentWeekProgress(expeditionId) }
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to fetch curriculum'
      console.error('Error fetching curriculum:', err)
    } finally {
      isLoading.value = false
    }
  }

  const fetchExpeditionSteps = async (expeditionId: string) => {
    isStepsLoading.value = true
    stepsError.value = null
    try {
      const response = await api.get(API_ROUTES.GET_EXPEDITION_PATH_STEPS(expeditionId))
      expeditionSteps.value = response.data
    } catch (err: any) {
      stepsError.value = err.response?.data?.message || 'Failed to fetch expedition steps'
      console.error('Error fetching expedition steps:', err)
    } finally {
      isStepsLoading.value = false
    }
  }

  const updateProgressStatus = async (id: number, status: string, expeditionId: string, weekId: string | null) => {
    isUpdating.value = true
    error.value = null

    try {
      await api.put(API_ROUTES.UPDATE_PROGRESS_STATUS(id.toString()), {
        new_status: status
      })

      // Since we always redirect to include week parameter, weekId should always be available
      // But we'll keep the fallback for safety
      if (weekId) {
        await fetchProgressByWeek(weekId, expeditionId);
     } else {
        await fetchCurrentWeekProgress(expeditionId);
     }

      // Wait for next DOM update cycle before setting loading false
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      nextTick(() => {
        isUpdating.value = false
      })
      return true
    } catch (err: any) {
      error.value = err.response?.data?.message || 'Failed to update progress status'
      console.error('Error updating progress status:', err)
      // Wait for next DOM update cycle before setting loading false
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      nextTick(() => {
        isUpdating.value = false
      })
      return false
    } 
  }

  // Helper function to update local notes state
  const updateLocalNote = (progressId: number, note: PersonalNote) => {
    personalNotes.value[progressId] = note
  }

  const createPersonalNote = async (progressId: number, notes: string) => {
    isNotesLoading.value = true
    notesError.value = null
    try {
      const response = await api.post(API_ROUTES.CREATE_PERSONAL_NOTE, {
        progress: progressId,
        notes: notes
      })
      if (response.data && response.data.id) {
        updateLocalNote(progressId, response.data as PersonalNote)
        return response.data as PersonalNote
      }
      return null
    } catch (err: any) {
      notesError.value = err.response?.data?.message || 'Failed to create note'
      console.error('Error creating note:', err)
      return null
    } finally {
      isNotesLoading.value = false
    }
  }

  const updatePersonalNote = async (noteId: number, progressId: number, notes: string) => {
    isNotesLoading.value = true
    notesError.value = null
    try {
      const response = await api.put(API_ROUTES.UPDATE_PERSONAL_NOTE(noteId), {
        progress: progressId,
        notes: notes
      })
      // Assuming the API returns the updated note object or confirms success
      // Update local state optimistically or based on response
      updateLocalNote(progressId, { id: noteId, notes: notes, progress: progressId }) 
      return true // Indicate success
    } catch (err: any) {
      notesError.value = err.response?.data?.message || 'Failed to update note'
      console.error('Error updating note:', err)
      // Optionally revert optimistic update here
      return false // Indicate failure
    } finally {
      isNotesLoading.value = false
    }
  }

  const fetchPersonalNote = async (progressId: number) => {
    // Avoid fetching if already loading
    if (isNotesLoading.value) {
      return;
    }
    isNotesLoading.value = true;
    notesError.value = null;
    try {
      const response = await api.get(API_ROUTES.GET_PERSONAL_NOTE(progressId));
      if (response.data && response.data.id) {
        // API might return a single note object or potentially an array with one item
        const note = Array.isArray(response.data) ? response.data[0] : response.data;
        if (note && note.id) {
           updateLocalNote(progressId, note as PersonalNote);
        }
      } else {
         // Handle cases where no note exists for this progressId
         // Maybe set a specific marker or leave it absent from personalNotes
         console.log(`No personal note found for progress ID: ${progressId}`);
      }
    } catch (err: any) {
      // If API returns 404 when note not found, treat it as non-error case
      if (err.response && err.response.status === 404) {
        console.log(`No personal note found for progress ID: ${progressId} (404)`);
      } else {
        notesError.value = err.response?.data?.message || 'Failed to fetch note';
        console.error('Error fetching personal note:', err);
      }
    } finally {
      isNotesLoading.value = false;
    }
  };

  const createPathStepReview = async (expeditionId: string, pathStepId: number, rating: number, reviewText: string) => {
    isReviewSubmitting.value = true
    reviewError.value = null

    try {
      await api.post(API_ROUTES.CREATE_PATH_STEP_REVIEW(expeditionId), {
        path_step: pathStepId,
        stars_value: rating,
        review_text: reviewText
      })
      return true
    } catch (err: any) {
      reviewError.value = err.response?.data?.message || 'Failed to submit review'
      console.error('Error submitting review:', err)
      return false
    } finally {
      isReviewSubmitting.value = false
    }
  }

  return {
    curriculum,
    isLoading,
    isUpdating,
    error,
    fetchCurriculumList,
    fetchCurrentWeekProgress,
    fetchProgressByWeek,
    activeWeek,
    updateProgressStatus,
    backlogCount,
    personalNotes,
    isNotesLoading,
    notesError,
    createPersonalNote,
    updatePersonalNote,
    fetchPersonalNote,
    expeditionSteps,
    isStepsLoading,
    stepsError,
    fetchExpeditionSteps,
    createPathStepReview,
    isReviewSubmitting,
    reviewError,
    isReordering,
  }
})
